"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, HelpCircle, Info } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import logger from "@/lib/logger";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { createAppkey, fetchUserAppkeys, updateAppkey } from "@/lib/tokenApi";
import useClientTranslation from "@/hooks/useClientTranslation";

import type { Group } from "@/components/GroupSidebar";

const QUICK_AMOUNT = [5, 10, 20, 50, 100, 200, 500];

const STORAGE_KEY = "token-groups";

// Days for quick expire options - labels will be translated in component
const QUICK_EXPIRE_DAYS = [1, 7, 30, 90, 180, 365];

interface Token {
  id: string;
  name: string;
  group: string;
  groupId?: string;
  billing: string;
  status: string;
  quota: number | string;
  used: number | string;
  model: string;
  created: string;
  limit: string;
}

interface CreateTokenModalProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onSuccess?: (newToken: Token) => void;
  token?: Token;
  mode?: "create" | "edit";
  currentGroupId?: string | null; // 当前选中的分组 ID
}

export default function CreateTokenModal({
  open,
  onClose,
  onSuccess,
  token,
  mode = "create",
  currentGroupId,
}: CreateTokenModalProps) {
  const { t } = useClientTranslation("tokenpg");
  const [tokenName, setTokenName] = useState("");
  const [unlimit, setUnlimit] = useState(false); // 改为默认启用限额（false = 有限额）
  const [forever, setForever] = useState(true);
  const [enableAdvanced, setEnableAdvanced] = useState(false);
  const [amount, setAmount] = useState<number | string>(10); // 默认 10 美元
  const [expireTime, setExpireTime] = useState("");
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<{ model_name: string }[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [enableFirstUse, setEnableFirstUse] = useState(false);
  const [allowFallback, setAllowFallback] = useState(false);
  const [ipWhitelist, setIpWhitelist] = useState("");
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupLoading, setGroupLoading] = useState(false);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);

  // Generate quick expire options with translated labels
  const quickExpireOptions = useMemo(() => {
    const dayLabels: Record<number, string> = {
      1: "1day",
      7: "7days", 
      30: "30days",
      90: "90days",
      180: "180days",
      365: "365days",
    };
    return QUICK_EXPIRE_DAYS.map(days => ({
      label: t(`quickExpire.${dayLabels[days]}`),
      value: days,
    }));
  }, [t]);

  const resolveGroupId = useCallback(
    (list: Group[]) => {
      if (mode === "edit" && token) {
        const matched = list.find(g => g.id === token.group || g.name === token.group);
        if (matched) return matched.id;
      }

      // 创建模式使用传入的 currentGroupId
      if (currentGroupId && list.some(g => g.id === currentGroupId)) {
        return currentGroupId;
      }

      if (selectedGroupId && list.some(g => g.id === selectedGroupId)) {
        return selectedGroupId;
      }

      return list[0]?.id ?? null;
    },
    [mode, selectedGroupId, token, currentGroupId]
  );

  const resetForm = useCallback(() => {
    setTokenName("");
    setUnlimit(true);
    setForever(true);
    setEnableAdvanced(false);
    setAmount("");
    setExpireTime("");
    setSelectedModels([]);
    setEnableFirstUse(false);
    setAllowFallback(false);
    setIpWhitelist("");
    setModelMenuOpen(false);
    setSelectedGroupId(resolveGroupId(groups));
  }, [resolveGroupId, groups]);

  const toDatetimeLocal = (s?: string | null) => {
    if (!s) return "";
    try {
      if (s.includes("T")) return s.slice(0, 16);
      if (s.includes(" ")) return s.replace(" ", "T").slice(0, 16);
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 16);
    } catch {
      // ignore
    }
    return "";
  };

  useEffect(() => {
    if (mode === "edit" && token) {
      setTokenName(token.name || "");
      // 兼容 quota 为数字或字符串
      setUnlimit(token.quota === "无限制" || token.quota === "长期有效");
      if (typeof token.quota === "number" && isFinite(token.quota)) {
        const num = token.quota / 500000;
        setAmount(Number.isInteger(num) ? String(num) : Number(num.toFixed(6)));
      } else {
        setAmount(
          typeof token.quota === "string" && token.quota.startsWith("$")
            ? token.quota.replace("$", "")
            : token.quota || ""
        );
      }
      const isForever = token.limit === "永不过期" || token.limit === "长期有效";
      setForever(isForever);
      setExpireTime(isForever ? "" : toDatetimeLocal(token.limit));
      setSelectedModels(token.model ? token.model.split(",").map(m => m.trim()) : []);
    } else if (!open) {
      resetForm();
    }
  }, [mode, token, open, resetForm]);

  // resolveGroupId 已移到文件顶部靠近 state 定义，避免在 resetForm 初始化时触发 TDZ 错误

  const loadGroups = useCallback(async () => {
    if (typeof window === "undefined") return;

    setGroupLoading(true);
    let options: Group[] = [];

    try {
      // 从 localStorage 读取，与 TokenGroupSidebar 保持一致
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Group[];
        // 过滤掉虚拟分组（如 default-local），只保留真实的应用 ID
        options = parsed.filter(
          g => g.id && !g.id.includes("default-local") && !g.id.startsWith("local-")
        );

        // 如果过滤后为空或者原本就有虚拟分组，重新从后端获取
        if (options.length === 0 || parsed.length > options.length) {
          logger.debug("检测到虚拟分组或空列表，从后端重新获取");
          const appkeys = await fetchUserAppkeys();
          options = (appkeys || [])
            .filter(
              (item: unknown) =>
                item &&
                typeof item === "object" &&
                item !== null &&
                "app_id" in item &&
                (item as Record<string, unknown>).app_id
            )
            .map((item: unknown) => {
              const rec = item as Record<string, unknown>;
              return {
                id: String(rec.app_id),
                name:
                  typeof rec.app_name === "string" && rec.app_name.trim()
                    ? rec.app_name
                    : "",
            };
          });
          localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
        }
      } else {
        // 如果本地没有，从后端获取
        const appkeys = await fetchUserAppkeys();

        // 只提取真实的应用 ID（有 app_id 的项）
        options = (appkeys || [])
          .filter(
            (item: unknown) =>
              item &&
              typeof item === "object" &&
              item !== null &&
              "app_id" in item &&
              (item as Record<string, unknown>).app_id
          )
          .map((item: unknown) => {
            const rec = item as Record<string, unknown>;
            return {
              id: String(rec.app_id),
              name:
                typeof rec.app_name === "string" && rec.app_name.trim()
                  ? rec.app_name
                  : "",
            };
          });

        // 不添加虚拟的 default-local
        localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
      }

      // 如果最后仍然没有分组，保持为空数组（用户需要创建分组）
      if (options.length === 0) {
      }
    } catch (err) {
      logger.error("加载分组失败:", err);
      // 出错时保持空数组，不创建虚拟分组
      options = [];
    }

    setGroups(options);
    setSelectedGroupId(resolveGroupId(options));
    setGroupLoading(false);
  }, [resolveGroupId]);

  // 创建模式下：弹窗打开/切换侧边栏分组时，默认选中当前分组
  useEffect(() => {
    if (!open) return;
    if (mode !== "create") return;
    if (!currentGroupId) return;
    if (!Array.isArray(groups) || groups.length === 0) return;
    if (!groups.some(g => g.id === currentGroupId)) return;

    // 只有当不同步时才更新，避免多余渲染
    if (selectedGroupId !== currentGroupId) {
      setSelectedGroupId(currentGroupId);
    }
  }, [open, mode, currentGroupId, groups, selectedGroupId]);

  useEffect(() => {
    if (open) {
      // 页面打开时清除虚拟分组
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as Group[];
          const hasVirtualGroups = parsed.some(g => g.id && g.id.includes("default-local"));
          if (hasVirtualGroups) {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (e) {
        logger.warn("清理 localStorage 失败:", e);
      }

      loadGroups();
    }
  }, [open, loadGroups]);

  useEffect(() => {
    if (!enableAdvanced) {
      setSelectedModels([]);
      setIpWhitelist("");
    }
  }, [enableAdvanced]);

  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return "-";
    const g = groups.find(g => g.id === selectedGroupId);
    if (!g) return "-";
    // 归一化显示名称：避免名称与 id 相同或为纯数字
    const name = (g.name || "").trim();
    if (!name || name === g.id) {
      return t("defaultGroup");
    }
    return name;
  }, [groups, selectedGroupId, t]);

  const handleCreate = async () => {
    if (!tokenName.trim()) {
      toast.error(t("createMessages.inputName"));
      return;
    }

    if (!unlimit && (!amount || Number(amount) <= 0)) {
      toast.error(t("createMessages.inputQuota"));
      return;
    }

    if (!forever && !expireTime) {
      toast.error(t("createMessages.selectExpire"));
      return;
    }

    try {
      setLoading(true);

      // 校验同分组下令牌名称不能重复
      const checkGroupId = mode === "edit" && token ? (token.groupId || token.group) : selectedGroupId;
      if (checkGroupId) {
        try {
          const existingTokens = await fetchUserAppkeys(checkGroupId);
          const trimmedName = tokenName.trim();
          const isDuplicate = (existingTokens || []).some((t: unknown) => {
            if (!t || typeof t !== "object") return false;
            const rec = t as Record<string, unknown>;
            const name = typeof rec.name === "string" ? rec.name.trim() : "";
            // 编辑模式下排除自身
            if (mode === "edit" && token) {
              const tokenKey = rec.key ?? rec.appkey ?? rec.id;
              if (tokenKey === token.id) return false;
            }
            return name === trimmedName;
          });
          if (isDuplicate) {
            toast.error(t("createMessages.duplicateName"));
            setLoading(false);
            return;
          }
        } catch (err) {
          logger.warn("校验令牌名称重复失败，跳过检查:", err);
        }
      }

      let newToken: Token;

      if (mode === "edit" && token) {
        // 编辑模式：更新令牌信息（传递 quota_enabled 与 expire_at）
        const expireAtStr =
          !forever && expireTime
            ? expireTime.includes("T")
              ? expireTime.replace("T", " ") + ":00"
              : expireTime
            : undefined;

        await updateAppkey(token.id, {
          name: tokenName.trim(),
          expire_at: expireAtStr,
          quota_limit: unlimit ? undefined : Number(amount) * 500000,
          quota_enabled: !unlimit,
        });

        newToken = {
          ...token,
          id: token.id,
          name: tokenName.trim(),
          group: selectedGroupName,
          billing: unlimit ? t("quotaUnlimited") : `${t("quotaLimited")} ${amount}`,
          quota: unlimit ? t("quotaUnlimited") : `${Number(amount).toFixed(2)}`,
          used: token.used,
          model: selectedModels.length > 0 ? selectedModels.join(", ") : t("allModels"),
          status: token.status,
          created: token.created,
          limit: forever ? t("longTermValid") : expireTime || token.limit,
        };

        toast.success(t("createMessages.updateSuccess"));
      } else {
        // 创建模式：新建令牌到当前分组
        if (!selectedGroupId) {
          toast.error(t("createMessages.selectGroup"));
          return;
        }

        // 防御性检查：确保分组 ID 是从后端获取到的有效分组（避免使用 local 临时分组）
        if (!groups || !groups.some(g => g.id === selectedGroupId)) {
          logger.warn("所选分组不在后端返回的分组列表中，阻止创建", selectedGroupId);
          toast.error(t("createMessages.invalidGroup"));
          return;
        }

        // 防御性检查：确保分组 ID 不是虚拟的
        if (selectedGroupId.includes("default-local") || selectedGroupId.startsWith("local-")) {
          logger.warn("检测到虚拟分组 ID:", selectedGroupId);
          toast.error(t("createMessages.localGroup"));
          return;
        }

        logger.debug("准备创建令牌，参数如下:", {
          name: tokenName.trim(),
          groupId: selectedGroupId,
          selectedModels,
        });

        // 将选中的 model_name 列表转换为后端需要的对象数组（若可用模型中找不到，则以 { model_name } 透传）
        const backendModels = selectedModels.map(name => {
          const found = availableModels.find(m => m.model_name === name);
          return { model_name: found?.model_name ?? name };
        });
        logger.debug("  - 将选中模型构建为后端对象:", { count: backendModels?.length ?? 0 });

        // 转换过期时间为后端格式 "YYYY-MM-DD HH:mm:ss"
        let expireAtStr: string | undefined;
        if (!forever && expireTime) {
          // expireTime 格式为 "2025-12-20T18:00"
          const dateObj = new Date(expireTime);
          expireAtStr = dateObj.toISOString().replace("T", " ").substring(0, 19);
        }

        const ipList =
          ipWhitelist
            .split(",")
            .map(ip => ip.trim())
            .filter(ip => ip.length > 0) || undefined;
        const billingConfig = {
          quota_enabled: !unlimit, // unlimit=false(限额) => quota_enabled=true ✓
          quota_limit: !unlimit && amount ? Number(amount) * 500000 : 0,
        };

        // billingConfig may contain sensitive info - avoid logging full objects
        logger.debug("billing config prepared", {
          billingConfigSummary: {
            quota_enabled: billingConfig.quota_enabled,
            quota_limit: billingConfig.quota_limit,
          },
        });

        // 调用新的后端接口
        const result = await createAppkey(
          tokenName.trim(),
          selectedGroupId,
          backendModels,
          expireAtStr,
          ipList,
          undefined, // modelParams 暂时不传
          billingConfig,
          true, // alive 默认启用
          undefined, // commonModelParams
          allowFallback
        );

        logger.info("创建令牌成功", { resultSummary: { id: result?.id, name: result?.name } });
        const displayGroupName =
          selectedGroupName && selectedGroupName !== "-" ? selectedGroupName : t("defaultGroup");

        // 从后端响应中提取模型名称用于展示
        const displayModels = (() => {
          const onModels = result?.appkey?.on_models;
          if (Array.isArray(onModels) && onModels.length > 0) {
            return onModels
              .map(m => {
                if (m && typeof m === "object" && "model_name" in (m as Record<string, unknown>)) {
                  const mn = (m as Record<string, unknown>).model_name;
                  return typeof mn === "string" ? mn : String(mn);
                }
                return String(m);
              })
              .join(", ");
          }
          if (selectedModels.length > 0) return selectedModels.join(", ");
          return t("allModels");
        })();

        newToken = {
          id: result?.appkey?.appkey || `token-${Date.now()}`,
          name: tokenName.trim(),
          group: displayGroupName,
          groupId: selectedGroupId,
          billing: unlimit ? t("quotaUnlimited") : `${t("quotaLimited")} ${amount}`,
          status: t("statusNormal"),
          quota: unlimit ? t("quotaUnlimited") : `${Number(amount).toFixed(2)}`,
          used: "$0.00",
          model: displayModels,
          created: new Date().toISOString().slice(0, 10),
          limit: forever ? t("longTermValid") : expireAtStr || "",
        };

        toast.success(t("createMessages.createSuccess"));
      }

      resetForm();
      onClose(false);
      // 编辑模式传递更新的令牌，创建模式传递空对象触发刷新
      if (mode === "edit") {
        onSuccess?.(newToken);
      } else {
        onSuccess?.({} as Token);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("创建令牌失败:", err);
        toast.error(t("createMessages.createFailed") + ": " + err.message);
      } else {
        logger.error("创建令牌失败:", String(err));
        toast.error(t("createMessages.createFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // resetForm 定义已移动到靠近 state 初始化的顶部，避免在 effect 注册时触发 TDZ 错误

  useEffect(() => {
    // 当 groups 列表改变时，确保 selectedGroupId 为正确默认值
    if (groups && groups.length > 0 && !selectedGroupId) {
      setSelectedGroupId(resolveGroupId(groups));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const handleClose = () => {
    resetForm();

    onClose(false);
  };

  // 清理 selectedModels helper 的未使用警告：实际在 UI 中通过 map 渲染并有删除按钮调用 removeModel
  // 将 removeModel 包裹为稳定函数以避免 lint 提示
  const removeModelRef = useCallback((value: string) => {
    setSelectedModels(prev => prev.filter(m => m !== value));
  }, []);

  const toggleModel = (value: string) => {
    setSelectedModels(prev =>
      prev.includes(value) ? prev.filter(m => m !== value) : [...prev, value]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? t("createModal.editTitle") : t("createModal.createTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 p-1">
          <div className="space-y-2">
            <Label>{t("createModal.tokenName")}</Label>
            <Input
              placeholder={t("createModal.tokenNamePlaceholder")}
              value={tokenName}
              onChange={e => setTokenName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent>{t("createModal.groupTooltip")}</TooltipContent>
              </Tooltip>
              <Label>{t("createModal.currentGroup")}</Label>
            </div>

            <div className="px-3 py-2 border border-border rounded-md bg-muted text-foreground">
              {groupLoading ? t("createModal.groupLoading") : selectedGroupName || t("defaultGroup")}
            </div>
            <p className="text-xs text-muted-foreground">{t("createModal.groupTip")}</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={unlimit} onCheckedChange={setUnlimit} />
              <Label>{t("createModal.unlimitedQuota")}</Label>
            </div>

            {!unlimit && (
              <div className="space-y-3 ml-8">
                <Label>{t("createModal.quota")}</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder={t("createModal.quotaPlaceholder")}
                />

                {/* 快捷额度 */}
                <div className="flex flex-wrap gap-2">
                  {QUICK_AMOUNT.map(a => (
                    <Button
                      key={a}
                      variant={amount == a ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAmount(a)}
                    >
                      {a}$
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 永不过期 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={forever} onCheckedChange={setForever} />
              <Label>{t("createModal.neverExpire")}</Label>
            </div>

            {/* 永不过期关闭 → 显示过期时间 */}
            {!forever && (
              <div className="space-y-3 ml-8">
                <Label>{t("createModal.expireTime")}</Label>
                <Input
                  type="datetime-local"
                  value={expireTime}
                  onChange={e => setExpireTime(e.target.value)}
                />

                {/* 快捷过期选项 */}
                <div className="flex flex-wrap gap-2">
                  {quickExpireOptions.map(opt => (
                    <Button
                      key={opt.value}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() + opt.value);
                        setExpireTime(d.toISOString().slice(0, 16));
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 启用高级选项 */}
          <div className="flex items-center gap-2">
            <Switch checked={enableAdvanced} onCheckedChange={setEnableAdvanced} />
            <Label>{t("createModal.advancedOptions")}</Label>
          </div>

          {/* 高级选项区域（可折叠） */}
          {enableAdvanced && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted">
              <div className="space-y-2">
                <Label>{t("createModal.availableModels")}</Label>
                <DropdownMenu
                  open={modelMenuOpen}
                  onOpenChange={open => {
                    setModelMenuOpen(open);
                    if (open && availableModels.length === 0) {
                      // fetch models on-demand
                      setLoadingModels(true);
                      fetch("/api/pricing/models")
                        .then(res => res.json())
                        .then((raw: any) => {
                          // 支持三种返回结构：[] | { data: [] } | { models: [] }
                          const src = Array.isArray(raw)
                            ? raw
                            : raw && Array.isArray(raw.data)
                            ? raw.data
                            : raw && Array.isArray(raw.models)
                            ? raw.models
                            : [];

                          const mapped = src.map((item: any) => ({
                            model_name: item?.model_name ?? item?.name ?? String(item ?? ""),
                          }));
                          setAvailableModels(mapped);
                        })
                        .catch(err => {
                          logger.error("/api/pricing/models fetch failed", err);
                          setAvailableModels([]);
                        })
                        .finally(() => setLoadingModels(false));
                    }
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full min-h-[42px] justify-start text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        {selectedModels.map(model => {
                          const modelLabel = model;
                          return (
                            <span
                              key={model}
                              role="button"
                              tabIndex={0}
                              title={`${t("createModal.deselectModel")} ${modelLabel}`}
                              aria-label={`${t("createModal.deselectModel")} ${modelLabel}`}
                              className="inline-flex items-center gap-1 border border-primary/30 bg-primary/10 px-2 py-1 text-xs text-primary cursor-pointer max-w-[160px] truncate"
                              onClick={e => {
                                e.stopPropagation();
                                removeModelRef(model);
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeModelRef(model);
                                }
                              }}
                            >
                              <span className="truncate max-w-[140px] block">{modelLabel}</span>
                            </span>
                          );
                        })}

                        {selectedModels.length === 0 && (
                          <span className="text-sm text-muted-foreground">{t("createModal.selectModels")}</span>
                        )}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    avoidCollisions={false}
                    className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-[200px] overflow-y-auto p-0"
                  >
                    {loadingModels ? (
                      <div className="p-3 text-sm text-muted-foreground">{t("createModal.loading")}</div>
                    ) : availableModels.length > 0 ? (
                      availableModels.map(opt => {
                        const active = selectedModels.includes(opt.model_name);

                        return (
                          <DropdownMenuItem
                            key={opt.model_name}
                            className={cn(
                              "flex items-center justify-between px-3 py-2 text-sm cursor-pointer rounded-none",
                              active
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-secondary"
                            )}
                            onSelect={event => {
                              event.preventDefault();
                              toggleModel(opt.model_name);
                            }}
                          >
                            <span className="truncate max-w-[220px] block">{opt.model_name}</span>
                            {active && (
                              <Check size={16} className="text-primary" />
                            )}
                          </DropdownMenuItem>
                        );
                      })
                    ) : (
                      <div className="p-3 text-sm text-muted-foreground">{t("createModal.noModels")}</div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={enableFirstUse} onCheckedChange={setEnableFirstUse} />
                <Label>{t("createModal.firstUseActivate")}</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={allowFallback} onCheckedChange={setAllowFallback} />
                <Label>{t("createModal.allowFallback")}</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-sm">
                    {t("createModal.fallbackTooltip")}
                  </TooltipContent>
                </Tooltip>
              </div>

              <div>
                <Label className="mb-2">{t("createModal.ipWhitelist")}</Label>
                <Input
                  placeholder={t("createModal.ipWhitelistPlaceholder")}
                  value={ipWhitelist}
                  onChange={e => setIpWhitelist(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* 提交按钮 */}
          <div className="flex gap-3 pt-3">
            <Button
              onClick={handleCreate}
              disabled={loading}
              className="flex-1"
            >
              {loading
                ? mode === "edit"
                  ? t("createModal.saving")
                  : t("createModal.creating")
                : mode === "edit"
                ? t("createModal.save")
                : t("createModal.create")}
            </Button>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              {t("createModal.cancel")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
